/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { resolveSkillDisplayDescription } from '../resolve-skill-display-description';
import { getIconColor } from '../skill-colors';
import { DefaultSkillIcon, skillIconMap } from '../skill-icons';
import type { RovoSkillTagProps } from '../skill-tag/SkillTag';
import { SkillTagInternal } from '../skill-tag/SkillTagInternal';
import messages from './messages';

/** Registry tag that marks a skill as being in beta. */
const BETA_SKILL_TAG = 'beta';

const hovercardStyles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
		textAlign: 'left',
	},
	containerScrollable: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
		textAlign: 'left',
		overflowY: 'auto',
	},
	skillNameRow: {
		marginBottom: token('space.100'),
		alignItems: 'center',
	},
	skillName: {
		fontWeight: token('font.weight.bold'),
		alignItems: 'center',
	},
	betaTag: {
		flexShrink: 0,
	},
	product: {
		marginTop: token('space.150'),
		color: token('color.text.subtle'),
		alignItems: 'center',
		textTransform: 'capitalize',
	},
});

export type SkillHoverCardContentProps = Omit<RovoSkillTagProps, 'slug' | 'hovercardMaxHeight'> & {
	maxHeight?: number | string;
	/** The skill's slug, e.g. "create-work-items". Required to render the slash command tag. */
	slug?: string;
};

/**
 * Kept as its own component so `useIntl` is only called when the lozenge actually renders.
 * The hover card has consumers that do not provide an `IntlProvider`, and they must keep
 * working while `custom_skills_beta_tags` is off — and for any skill that is not in beta.
 */
const SkillBetaLozenge = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={hovercardStyles.betaTag} testId="skill-hovercard-beta-lozenge">
			<Lozenge appearance="discovery">{formatMessage(messages.skillBetaTag)}</Lozenge>
		</Box>
	);
};

export const SkillHoverCardContent = ({
	product,
	iconName,
	maxHeight,
	slug,
	...props
}: SkillHoverCardContentProps): JSX.Element => {
	const displayDescription = resolveSkillDisplayDescription(props.helpText, props.description);
	const iconFromMap = iconName ? skillIconMap[iconName] : undefined;
	const IconComponent =
		iconFromMap ?? (fg('skills_fe_display_slash_command') ? DefaultSkillIcon : undefined);
	const shouldRenderSkillTagName = fg('skills_fe_display_slash_command') && Boolean(slug);
	const shouldUseSlantedWrapping = !fg('rovo_skill_tag_slanted_background_killswitch');
	const shouldShowBetaTag =
		fg('custom_skills_beta_tags') &&
		// The two skill APIs surface registry tags with different casing, so compare
		// case-insensitively: plain strings from ConvoAiSkill, dimension display names
		// (e.g. `BETA`) from the component directory.
		(props.tags?.some((tag) => tag.toLowerCase() === BETA_SKILL_TAG) ?? false);

	return (
		<Box
			testId="skill-hovercard-content"
			xcss={
				maxHeight !== undefined ? hovercardStyles.containerScrollable : hovercardStyles.container
			}
			style={{ maxHeight }}
		>
			<Stack>
				<Inline
					xcss={hovercardStyles.skillNameRow}
					spread="space-between"
					alignBlock="center"
					space="space.200"
				>
					<Inline xcss={hovercardStyles.skillName} space="space.100" alignBlock="center">
						{IconComponent && (
							<Suspense fallback={null} interactionName="skill-tag-hovercard-icon">
								<IconComponent
									label=""
									size="medium"
									color={getIconColor(props?.color, props?.invertColors)}
								/>
							</Suspense>
						)}
						{shouldRenderSkillTagName ? (
							<SkillTagInternal
								// shouldRenderSkillTagName already checked Boolean(slug), so slug is non-empty here.
								slug={slug!}
								name={props.name}
								color={props.color}
								invertColors={props.invertColors}
								shouldUseSlantedWrapping={shouldUseSlantedWrapping}
							/>
						) : (
							<Heading size="xsmall">{props.name}</Heading>
						)}
					</Inline>
					{shouldShowBetaTag && <SkillBetaLozenge />}
				</Inline>
				{displayDescription && (
					<Box>
						<Text color="color.text">{displayDescription}</Text>
					</Box>
				)}
				{product && (
					<Inline xcss={hovercardStyles.product} space="space.075">
						{props.productIconUrl && (
							<Image
								src={props.productIconUrl}
								testId={`${product}-icon`}
								alt=""
								width={16}
								height={16}
							/>
						)}
						<Text color="color.text.subtle" size="small">
							{product}
						</Text>
					</Inline>
				)}
			</Stack>
		</Box>
	);
};
